import React, { useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Search } from 'lucide-react';
import { searchCie10 } from '../lib/cie10';

/**
 * Buscador de diagnósticos CIE-10.
 *
 * Sustituye a escribir el diagnóstico y el código de memoria. Se busca por
 * nombre, por código o por como lo dice la gente ("colon irritable"), y al
 * elegir se rellenan los dos campos a la vez.
 *
 * Sigue permitiendo escribir libremente: hay diagnósticos que no están en el
 * catálogo y el médico no puede quedarse bloqueado por eso.
 *
 * @param {string} diagnosis - Texto del diagnóstico
 * @param {string} code - Código CIE-10
 * @param {Function} onChange - ({diagnosis, cie10_code})
 * @param {boolean} [disabled]
 */
export default function Cie10Search({ diagnosis, code, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const blurTimer = useRef(null);

  const results = useMemo(() => searchCie10(query), [query]);

  const pick = (entry) => {
    onChange({ diagnosis: entry.name, cie10_code: entry.code });
    setQuery('');
    setOpen(false);
  };

  const handleType = (value) => {
    setQuery(value);
    setOpen(true);
    // Lo tecleado es el diagnóstico mientras no se elija del catálogo: si el
    // médico escribe algo que no está en la lista, no se pierde.
    onChange({ diagnosis: value, cie10_code: code });
  };

  // El clic en un resultado dispara el blur del input antes que el click, así
  // que el cierre se retrasa lo justo para que el clic llegue.
  const handleBlur = () => {
    blurTimer.current = setTimeout(() => setOpen(false), 150);
  };
  const cancelClose = () => clearTimeout(blurTimer.current);

  return (
    <div className="flex gap-2">
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          value={query || diagnosis || ''}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => { setQuery(''); setOpen(true); }}
          onBlur={handleBlur}
          placeholder="Buscar diagnóstico o código..."
          className="pl-9"
          disabled={disabled}
          autoComplete="off"
        />

        {open && results.length > 0 && (
          <ul
            onMouseDown={cancelClose}
            className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto"
          >
            {results.map((entry) => (
              <li key={entry.code}>
                <button
                  type="button"
                  onClick={() => pick(entry)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-baseline gap-2"
                >
                  <span className="font-mono text-xs text-blue-700 shrink-0 w-16">{entry.code}</span>
                  <span className="text-sm text-slate-700">{entry.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Input
        value={code || ''}
        onChange={(e) => onChange({ diagnosis, cie10_code: e.target.value })}
        placeholder="CIE-10"
        className="w-28 shrink-0 font-mono text-sm"
        disabled={disabled}
      />
    </div>
  );
}
